import { AxiosError } from 'axios';

// We stamp this onto each request config in the axios request interceptor so that,
//  if the request later fails, we can tell how long it ran before failing. This is
//  especially useful for diagnosing timeouts and backend slowdowns/thread-starvation.
declare module 'axios' {
    export interface InternalAxiosRequestConfig {
        metadata?: { startTime: number };
    }
}

// A flat, JSON-serializable summary of the genuinely useful fields of an AxiosError.
//  Unlike the raw error (which Sentry renders as `[object Object]` and whose `request`
//  is an XMLHttpRequest that JSON.stringify turns into `{}`), every field here survives
//  the trip to Sentry/Datadog and is low-cardinality enough to search/aggregate on.
export interface AxiosErrorContext {
    isAxiosError: boolean;
    code?: string; // e.g. ECONNABORTED, ERR_NETWORK
    message: string; // e.g. "timeout exceeded"
    method?: string;
    url?: string; // full URL incl. the backend prefix added by the request interceptor
    timeoutMs?: number; // configured timeout (0/undefined means none)
    requestDurationMs?: number; // how long the request ran before failing
    responseStatus?: number;
    responseStatusText?: string;
    responseData?: string; // truncated; only present when the backend actually responded
}

type MaybeAxiosError = AxiosError | (Error & { isAxiosError: undefined });
export const isAxiosError = (error: Error | AxiosError): error is AxiosError =>
    !!(error as MaybeAxiosError).isAxiosError;

// Cap response bodies so we don't dump huge (and potentially sensitive) payloads.
const MAX_RESPONSE_DATA_CHARS = 2000;

const stringifyResponseData = (data: unknown): string | undefined => {
    if (data === undefined || data === null) {
        return undefined;
    }
    let text: string;
    try {
        text = typeof data === 'string' ? data : JSON.stringify(data);
    } catch {
        text = String(data);
    }
    if (text.length > MAX_RESPONSE_DATA_CHARS) {
        return `${text.slice(0, MAX_RESPONSE_DATA_CHARS)}… (truncated)`;
    }
    return text;
};

export const extractAxiosErrorContext = (
    error: Error | AxiosError | null,
): AxiosErrorContext | undefined => {
    if (!error) {
        return undefined;
    }

    if (!isAxiosError(error)) {
        return { isAxiosError: false, message: error.message };
    }

    const startTime = error.config?.metadata?.startTime;
    const requestDurationMs =
        startTime !== undefined ? Math.round(performance.now() - startTime) : undefined;

    return {
        isAxiosError: true,
        code: error.code,
        message: error.message,
        method: error.config?.method,
        url: error.config?.url,
        timeoutMs: error.config?.timeout,
        requestDurationMs,
        responseStatus: error.response?.status,
        responseStatusText: error.response?.statusText,
        responseData: stringifyResponseData(error.response?.data),
    };
};

// A compact one-liner suitable for embedding into an error message, so the Sentry/Datadog
//  event *title itself* is informative instead of just `[object Object]`.
//  e.g. "timeout exceeded (ECONNABORTED) GET /visits/123 after 30002ms → status 502"
export const formatAxiosErrorSummary = (ctx: AxiosErrorContext): string => {
    const parts: string[] = [ctx.message];
    if (ctx.code) {
        parts.push(`(${ctx.code})`);
    }
    if (ctx.method || ctx.url) {
        parts.push(`${(ctx.method ?? '').toUpperCase()} ${ctx.url ?? ''}`.trim());
    }
    if (ctx.requestDurationMs !== undefined) {
        parts.push(`after ${ctx.requestDurationMs}ms`);
    }
    parts.push(
        ctx.responseStatus !== undefined ? `→ status ${ctx.responseStatus}` : '→ no response',
    );
    return parts.join(' ');
};
